import * as React from "react";
import { m, LazyMotion, domMax } from "framer-motion";
import { ConfirmActionModal, ChunkSelection, Button } from "ui/components";
import { useEditorContext } from "ui/context/EditorContext";
import { Selection } from "core/models";
import styles from "./EditorSelections.module.scss";

interface Props {
    onEnableTrim(selection: Selection): void;
    onPreview(): void;
}

interface Position {
    top: number;
    height: number;
}

interface ItemProps {
    setPosition(index: number, pos: Position): void;
    moveItem(index: number, ypos: number): void;
    i: number;
    children?: React.ReactNode;
}

const Item: React.FC<ItemProps> = ({ setPosition, moveItem, i, children }) => {
    const [isDragging, setDragging] = React.useState(false);

    const ref = React.useRef<HTMLLIElement>(null);

    React.useEffect(() => {
        if (!ref.current) {
            return;
        }
        const newPos: Position = {
            height: ref.current.offsetHeight,
            top: ref.current.offsetTop,
        };
        setPosition(i, newPos);
    }, [i, setPosition]);

    const onTop = { originZ: 1 };
    const flat = {
        originZ: 0,
    };

    return (
        <LazyMotion features={domMax} strict>
            <m.li
                layout
                ref={ref}
                initial={false}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 1.05 }}
                whileDrag={{ rotate: "-1deg" }}
                drag="y"
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={0.4}
                onDragStart={() => setDragging(true)}
                onDragEnd={() => setDragging(false)}
                onDrag={(e, { point }) => moveItem(i, point.y)}
                style={isDragging ? onTop : flat}
            >
                {children}
            </m.li>
        </LazyMotion>
    );
};

const EditorSelections: React.FC<Props> = ({ onEnableTrim, onPreview }) => {
    const editorContext = useEditorContext();
    const [selectionIDSelectedForDelete, setSelectedForDelete] = React.useState<
        string | null
    >(null);
    const [scrollable, setScrollable] = React.useState<boolean>(false);
    const dropContainerRef = React.useRef<HTMLUListElement>(null);

    React.useEffect(() => {
        if (!dropContainerRef.current) {
            return;
        }
        const updateScrollable = () => {
            if (
                dropContainerRef.current &&
                dropContainerRef.current.scrollHeight >
                    dropContainerRef.current.clientHeight
            ) {
                setScrollable(true);
            } else {
                setScrollable(false);
            }
        };
        updateScrollable();

        window.addEventListener("resize", updateScrollable);
        return () => {
            window.removeEventListener("resize", updateScrollable);
        };
    }, []);

    const addNewSelection = () => {
        const selections = [...editorContext.selections];
        const newSelection = new Selection();
        selections.push(newSelection);
        editorContext.setPreviewing(false);
        editorContext.setSelections(selections);
        editorContext.setCurrentSelectionID(newSelection.id);
    };

    const confirmDelete = (shouldDelete: boolean) => {
        if (!shouldDelete) {
            setSelectedForDelete(null);
            return;
        }
        const selections = [...editorContext.selections];
        const idx = selections.findIndex(
            (x) => x.id === selectionIDSelectedForDelete,
        );
        if (idx !== -1) {
            selections.splice(idx, 1);
            if (selections.length === 0) {
                selections.push(new Selection());
            }
            const newSelectionID = selections[selections.length - 1].id;
            editorContext.setCurrentSelectionID(newSelectionID);
            editorContext.setPreviewing(false);
            editorContext.setSelections(selections);
        }
        setSelectedForDelete(null);
    };

    const setDeleteHandler = React.useCallback((selectionId: string) => {
        setSelectedForDelete(selectionId);
    }, []);

    const handleSort = (selectionId: string, newIndex: number) => {
        const selections = [...editorContext.selections];
        const currentIdx = selections.findIndex((x) => x.id === selectionId);
        const selection = selections.find((s) => s.id === selectionId);
        if (!selection || currentIdx === -1) {
            return;
        }

        selections.splice(currentIdx, 1);
        selections.splice(newIndex, 0, selection);
        editorContext.setSelections(selections);
    };

    const handleSelectionClick = React.useCallback(
        (selectionID: string) => {
            editorContext.setCurrentSelectionID(selectionID);
            editorContext.setPreviewing(false);
        },
        [editorContext],
    );

    const { selections } = editorContext;

    const positions = React.useRef<Position[]>([]).current;
    const updatePosition = (i: number, offset: Position) => {
        positions[i] = offset;
    };

    const getIndex = (targetIdx: number, yOffset: number): number => {
        const targetBottom =
            positions[targetIdx].top + positions[targetIdx].height;
        if (yOffset < targetBottom) {
            const prevItem = positions[targetIdx - 1];
            if (prevItem === undefined) {
                return targetIdx;
            }
            const prevTop = prevItem.top + prevItem.height;
            if (yOffset < prevTop) {
                return targetIdx - 1;
            }
        }
        if (yOffset > targetBottom) {
            const nextItem = positions[targetIdx + 1];
            if (nextItem === undefined) {
                return targetIdx;
            }
            const nextBottom = nextItem.top + nextItem.height;
            if (yOffset > nextBottom) {
                return targetIdx + 1;
            }
        }
        return targetIdx;
    };

    const moveItem = (i: number, dragOffset: number) => {
        const targetIndex = getIndex(i, dragOffset);
        if (targetIndex !== i) {
            handleSort(selections[i].id, getIndex(i, dragOffset));
        }
    };

    const playableSelectionAvailable =
        selections.filter((selection) => selection.segments.length > 0)
            .length !== 0;

    return (
        <>
            {selectionIDSelectedForDelete && (
                <ConfirmActionModal
                    theme="dark"
                    title="Delete selection"
                    description="Are you sure you want to delete the selection?"
                    confirmBtnText="Delete"
                    handleCancel={() => confirmDelete(false)}
                    handleConfirm={() => confirmDelete(true)}
                />
            )}
            <ul
                className={styles.dropContainer}
                ref={dropContainerRef}
                style={
                    scrollable
                        ? { overflowY: "scroll" }
                        : { overflowY: "visible" }
                }
            >
                {selections.map((selection, index) => (
                    <Item
                        key={selection.id}
                        i={index}
                        setPosition={updatePosition}
                        moveItem={moveItem}
                    >
                        <ChunkSelection
                            isActive={
                                selection.id ===
                                editorContext.currentSelectionID
                            }
                            isSelected={
                                selection.id ===
                                editorContext.currentSelectionID
                            }
                            selection={selection}
                            onDeleteHandler={setDeleteHandler}
                            onActiveClick={handleSelectionClick}
                            onEnableTrimHandler={() => onEnableTrim(selection)}
                            className="chunkSelection"
                            small={scrollable}
                        />
                    </Item>
                ))}
            </ul>
            <Button
                type="secondary"
                theme="light"
                icon="plus"
                className={styles.previewBtn}
                onClick={addNewSelection}
            >
                Add selection
            </Button>
            <Button
                type="secondary"
                theme="light"
                icon="play"
                className={styles.previewBtn}
                onClick={onPreview}
                disabled={
                    editorContext.previewing || !playableSelectionAvailable
                }
            >
                {editorContext.previewing ? "Previewing" : "Play 1-by-1"}
            </Button>
        </>
    );
};

export default EditorSelections;
